if (!customElements.get('bewo-community-videos')) {
  class BewoCommunityVideos extends HTMLElement {
    constructor() {
      super();
      this.sliderInstance = false;
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.onSectionLoad = this.onSectionLoad.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.section = this.closest('.section') || this.closest(`[class*="section-${this.sectionId}"]`);
      this.shell = this.closest('.bewo-community-videos__shell');
      this.sliderWrapper = this.querySelector('.bewo-community-videos__items');
      this.gap = Number(this.dataset.gap || 24);
      this.autoplayDelay = Number(this.dataset.autoplayDelay || 7000);
      this.slides = Array.from(this.querySelectorAll('.bewo-community-videos__slide'));

      this.initVideoHoverPlayback();

      if (this.slides.length <= 1) return;

      document.addEventListener('shopify:section:load', this.onSectionLoad);
      this.waitForTheme(() => this.initSlider(true));
    }

    disconnectedCallback() {
      this.destroySlider();
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:section:load', this.onSectionLoad);
    }

    onSectionLoad(event) {
      if (event.detail.sectionId !== this.sectionId) return;
      this.destroySlider();
      this.waitForTheme(() => this.initSlider(true));
    }

    waitForTheme(callback) {
      if (window.BewoTheme?.Carousel && window.BewoTheme?.Swiper) {
        callback();
        return;
      }

      requestAnimationFrame(() => this.waitForTheme(callback));
    }

    initVideoHoverPlayback() {
      this.querySelectorAll('.bewo-community-video-card__media').forEach((media) => {
        const video = media.querySelector('.bewo-community-video-card__video');
        if (!video) return;

        const card = media.closest('.bewo-community-video-card');

        video.pause();
        video.removeAttribute('autoplay');

        const playVideo = () => {
          video.play().catch(() => {});
        };

        const pauseVideo = () => {
          video.pause();
        };

        media.addEventListener('pointerenter', playVideo);
        media.addEventListener('pointerleave', pauseVideo);

        video.addEventListener('play', () => card?.classList.add('is-playing'));
        video.addEventListener('pause', () => card?.classList.remove('is-playing'));
      });
    }

    getNavigation() {
      const root = this.shell || this.section || this.parentElement;
      return {
        nextEl: root?.querySelector('.swiper-btn-next'),
        prevEl: root?.querySelector('.swiper-btn-prev'),
      };
    }

    initSlider(force = false) {
      if (!force && typeof this.sliderInstance === 'object') return;
      if (!window.BewoTheme?.Carousel || !this.sliderWrapper) return;

      this.destroySlider();

      this.classList.add('swiper');
      this.sliderWrapper.classList.add('swiper-wrapper');

      const { nextEl, prevEl } = this.getNavigation();
      const autoplayOptions = Shopify.designMode
        ? false
        : {
            delay: this.autoplayDelay,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          };

      this.sliderInstance = new window.BewoTheme.Carousel(
        this,
        {
          slidesPerView: 'auto',
          spaceBetween: this.gap,
          loop: false,
          rewind: true,
          speed: 900,
          threshold: 4,
          watchOverflow: false,
          centeredSlides: false,
          slideToClickedSlide: false,
          grabCursor: true,
          allowTouchMove: true,
          simulateTouch: true,
          observer: true,
          observeParents: true,
          autoplay: autoplayOptions,
          navigation: {
            nextEl,
            prevEl,
          },
        },
        window.BewoTheme.Swiper.Autoplay
      );

      this.sliderInstance.init();

      const refresh = () => {
        const slider = this.sliderInstance?.slider;
        if (!slider) return;
        slider.update();
        slider.slideTo(0, 0);
        if (autoplayOptions && slider.autoplay && !slider.autoplay.running) {
          slider.autoplay.start();
        }
      };

      requestAnimationFrame(refresh);
      window.setTimeout(refresh, 350);
      window.addEventListener('load', refresh, { once: true });

      if (Shopify.designMode) {
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }
    }

    onBlockSelect(event) {
      if (String(event.detail.sectionId) !== String(this.sectionId)) return;
      const index = Number(event.target.closest('[data-index]')?.dataset.index);
      if (Number.isNaN(index) || !this.sliderInstance?.slider) return;
      this.sliderInstance.slider.slideTo(index);
    }

    destroySlider() {
      if (typeof this.sliderInstance !== 'object' || !this.sliderInstance?.slider) return;

      this.sliderInstance.slider.destroy(true, true);
      this.sliderInstance = false;
    }
  }

  customElements.define('bewo-community-videos', BewoCommunityVideos);
}
